import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { API_URL } from 'constant';
import { loginState } from 'global-stores';
import { useNetwork, useNotification } from 'hooks';

export interface IDrawerData {
	/** /complex-session-details — metadata, activities, proof docs */
	detail: any;
	/** node payloads keyed by stepId: authentication, kyc, kyb, accreditation, aml, signAgreement */
	steps: Record<string, any>;
	/* EVERY payload per step, in journey order. `steps` keeps ONE per stepId,
	   which is right for a step that occurs once and silently wrong for one
	   that occurs twice: a flow with two Accreditation nodes (506(b) then
	   506(c)) fetched both and then overwrote the first with the second, so
	   the drawer showed one packet and lost the completed, signed one. */
	stepsAll: Record<string, any[]>;
}

const EMPTY: IDrawerData = { detail: null, steps: {}, stepsAll: {} };

/** The steps whose payloads the drawer actually draws. */
/* `authentication` carries the liveness video, the selfie and the face-match
   score. Its faceVideoURL is a SIGNED GCS url the API recomputes per read, which
   is why it is fetched with the rest rather than cached anywhere. */
const WANTED = [
	'authentication',
	'kyc',
	'kyb',
	'accreditation',
	'aml',
	// The executed document. Its node carries the signed PDF's url, the envelope
	// status and the completed field data — none of which the drawer showed at
	// all before, on flows whose entire purpose is that document.
	'signAgreement',
	// The applicant's own answers. `dynamicForm` and `form` are different steps
	// with different action keys — the builder produces one or the other — and
	// neither was fetched, so the questionnaire never appeared on the page.
	'dynamicForm',
	'form',
];

/**
 * One session's detail.
 *
 * Two rounds: /complex-session-details for the session information and the
 * activity log, then one /session-nodes call per step the drawer draws —
 * authentication, KYC, KYB, accreditation, AML. Steps the session never reached
 * are skipped, so a
 * three-step session costs three requests, not ten.
 *
 * Opening also marks the session viewed (`viewed=true`), which is what clears
 * it from the tab badges — the same side effect the old details page had.
 */
export const useSessionDrawer = (sessionId: string | null) => {
	const { get, post } = useNetwork({ updateState: false });
	const { patch } = useNetwork({ updateState: false, returnResponse: true });
	const { successNotification, errorNotification } = useNotification();
	const login = useRecoilValue(loginState) ?? {};

	const [data, setData] = useState<IDrawerData>(EMPTY);
	const [loaded, setLoaded] = useState(false);
	const [posting, setPosting] = useState(false);
	const generation = useRef(0);

	const load = useCallback(async () => {
		if (!sessionId) {
			setData(EMPTY);
			return;
		}
		const mine = ++generation.current;
		setLoaded(false);
		setData(EMPTY);

		const detailResp = await get(
			`${API_URL.COMPLEX_SESSION_DETAILS}/${sessionId}?viewed=true`
		).catch(() => null);
		if (mine !== generation.current) return;

		const detail = detailResp?.data ?? null;
		setData({ detail, steps: {}, stepsAll: {} });
		setLoaded(true);

		const nodes: any[] = (detail?.nodes ?? []).filter(
			(n: any) => n?.visited && WANTED.includes(n?.stepId)
		);

		// Journey order, so two nodes of one step read in the order the applicant
		// met them; the details payload lists them in no particular order.
		const started = (n: any) => Number(n?.action?.stepStartTime) || 0;
		nodes.sort((a, b) => started(a) - started(b));

		const payloads = await Promise.all(
			nodes.map(n =>
				get(`${API_URL.Session_Nodes}/${sessionId}?nodeId=${n._id}`)
					.then(r => ({ stepId: n.stepId, data: r?.data ?? r }))
					.catch(() => null)
			)
		);
		if (mine !== generation.current) return;

		setData(prev => ({
			...prev,
			steps: payloads.reduce((acc: Record<string, any>, p) => {
				if (p?.stepId) acc[p.stepId] = p.data;
				return acc;
			}, {}),
			stepsAll: payloads.reduce((acc: Record<string, any[]>, p) => {
				if (p?.stepId) acc[p.stepId] = [...(acc[p.stepId] ?? []), p.data];
				return acc;
			}, {}),
		}));
		// useNetwork's `get` identity churns every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionId]);

	useEffect(() => {
		load();
	}, [load]);

	/**
	 * A comment is an activity with `type: 'comment'` — the same PATCH the old
	 * details page made, against the step the session is actually sitting on so
	 * the note lands where a reviewer will look for it.
	 */
	const comment = useCallback(
		async (
			text: string,
			opts: { sendMail: boolean; stepId?: string; nodeId?: string }
		) => {
			if (!sessionId || !text.trim()) return false;
			setPosting(true);
			const resp = await patch(`${API_URL.ACTIVITIES}/${sessionId}`, {
				/* stepId and nodeId travel together or not at all — the API
				   answers 400 "Node id is required" for a stepId sent alone.
				   Neither means a comment on the session itself. */
				...(opts.stepId && opts.nodeId ? { stepId: opts.stepId } : {}),
				to: '',
				from: '',
				data: text,
				type: 'comment',
				userName: login?.name ?? login?.firstName ?? 'Admin',
				sendMail: opts.sendMail,
				recipientEmail: data?.detail?.metadata?.deviceInfo?.email ?? '',
				flow: 'complex',
				...(opts.stepId && opts.nodeId ? { nodeId: opts.nodeId } : {}),
			}).catch(() => null);
			setPosting(false);

			// `patch` is the ONE verb in useNetwork that never implements
			// `returnResponse` (get/post/put/formData/remove all do), so it returns
			// the bare payload — never the `{response, apiData}` shape this was
			// checking for. Even a 200 therefore fell through to the error toast and
			// skipped the reload, which is what made a comment that HAD saved look
			// like it had failed. The activity comes back carrying its own id.
			if (resp?.id || resp?._id || resp?.data?.id) {
				successNotification('Comment added');
				load();
				return true;
			}
			errorNotification('Could not add the comment');
			return false;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sessionId, data, login, load]
	);

	/**
	 * Set one step's status — the compliance decision the old details page made
	 * from its per-section dropdown (views/sessions/components/action.tsx).
	 *
	 * Same endpoint as a comment, same activity record: what makes it a decision
	 * rather than a note is `type: 'status'` plus `to` (the new status) and
	 * `from` (the one it had). pipelines.api's complex-session-nodes reads those
	 * to move the node, fire the webhook and write the audit entry — so this is
	 * the real control, not a display.
	 */
	const setStatus = useCallback(
		async (opts: {
			to: string;
			from: string;
			stepId: string;
			nodeId?: string;
			note?: string;
		}) => {
			if (!sessionId || !opts.stepId) return false;
			setPosting(true);
			const resp = await patch(`${API_URL.ACTIVITIES}/${sessionId}`, {
				stepId: opts.stepId,
				to: opts.to,
				from: opts.from ?? '',
				data: opts.note ?? '',
				type: 'status',
				userName: login?.name ?? login?.firstName ?? 'Admin',
				sendMail: false,
				recipientEmail: data?.detail?.metadata?.deviceInfo?.email ?? '',
				flow: 'complex',
				...(opts.nodeId ? { nodeId: opts.nodeId } : {}),
			}).catch(() => null);
			setPosting(false);

			if (resp?.id || resp?._id || resp?.data?.id) {
				successNotification('Status updated');
				load();
				return true;
			}
			errorNotification('Could not update the status');
			return false;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sessionId, data, login, load]
	);

	/**
	 * Reviewers this business can assign a session to — GET /assignee-users,
	 * the same list the old details page's assign modal read.
	 */
	const [reviewers, setReviewers] = useState<any[]>([]);
	useEffect(() => {
		let alive = true;
		get(API_URL.ASSIGNEE_USERS)
			.then((res: any) => {
				if (alive) setReviewers(res?.data ?? []);
			})
			.catch(() => undefined);
		return () => {
			alive = false;
		};
		// useNetwork's `get` identity churns every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Hand the session to a reviewer. POST /session-assignee takes an ARRAY of
	 * session ids — the old page assigned from the list, in bulk; here it is
	 * always the one session the drawer is showing.
	 */
	/**
	 * Assign a risk level (high / medium / low).
	 *
	 * Goes through the SAME `PATCH /activities/:id` the legacy header uses, as a
	 * `comment` carrying `priorityRisk` + `previousPriorityRisk` — that pairing is
	 * what makes the change render as a risk row in the activity log rather than
	 * as an empty comment. `data` is deliberately an empty string, matching the
	 * legacy payload; the log builds its own wording from the two risk fields.
	 */
	const setRisk = useCallback(
		async (to: string, from: string) => {
			if (!sessionId || !to) return false;
			setPosting(true);
			const resp = await patch(`${API_URL.ACTIVITIES}/${sessionId}`, {
				data: '',
				type: 'comment',
				userName: login?.name ?? login?.firstName ?? 'Admin',
				sendMail: false,
				requiredAttachment: false,
				recipientEmail: data?.detail?.metadata?.deviceInfo?.email ?? '',
				bcc: [],
				cc: [],
				flow: 'complex',
				priorityRisk: to,
				previousPriorityRisk: from || 'unassigned',
			}).catch(() => null);
			setPosting(false);

			if (resp?.id || resp?._id || resp?.data?.id) {
				successNotification(`Risk level set to ${to}`);
				load();
				return true;
			}
			errorNotification('Could not set the risk level');
			return false;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sessionId, login, data?.detail, load]
	);

	const assign = useCallback(
		async (assignee: string, note: string) => {
			// `comment` is a required, non-empty string on the API — guard here so a
			// caller cannot turn it into a 400.
			if (!sessionId || !assignee || !note?.trim()) return false;
			setPosting(true);
			const resp = await post(API_URL.SESSION_ASSIGNEE, {
				assignee,
				sessionIds: [sessionId],
				comment: note.trim(),
			}).catch(() => null);
			setPosting(false);

			if (resp?.success || resp?.data || resp?.message === 'ok') {
				successNotification('Session assigned');
				load();
				return true;
			}
			errorNotification('Could not assign the session');
			return false;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sessionId, load]
	);

	/* The old accreditation panel's "Send document for signing" -- re-sends
	   the e-sign invite for a packet still under review (RESEND_DOC_INVITES).
	   Carried over as-is: same endpoint, same success/failure wording. */
	const resendAccreditationInvite = useCallback(
		async (envelopeId: string): Promise<boolean> => {
			const resp = await get(
				`${API_URL.RESEND_DOC_INVITES}/${envelopeId}`
			).catch(() => null);
			if (resp) {
				successNotification('Document send successfully');
				return true;
			}
			errorNotification('something went wrong');
			return false;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return {
		...data,
		loaded,
		posting,
		comment,
		setStatus,
		setRisk,
		reviewers,
		assign,
		reload: load,
		resendAccreditationInvite,
	};
};
